/**
 * For the inventory reports over a period (used with inventoryReportMixin): From / To dates with quick ranges,
 * and the in-vs-out bar chart of `summary.trend` ([{ date, in_qty, out_qty }], by `summary.trend_unit`).
 * The component's `filters` hold date_from / date_to (see defaultPeriod()).
 */
import { localToday } from './inventoryReportMixin';

export const IN_COLOR = '#28C76F';
export const OUT_COLOR = '#EA5455';

function isoDate(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function addDays(iso, days) {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return isoDate(date);
}

/** This month up to today. */
export function defaultPeriod(today = localToday()) {
  return { date_from: `${today.slice(0, 8)}01`, date_to: today };
}

export default {
  data() {
    return { today: localToday() };
  },

  computed: {
    quickRanges() {
      const today = this.today;
      const lastMonthEnd = addDays(`${today.slice(0, 8)}01`, -1);
      return [
        { key: 'today', label: 'Today', from: today, to: today },
        { key: '7d', label: 'Last 7 days', from: addDays(today, -6), to: today },
        { key: 'month', label: 'This month', from: `${today.slice(0, 8)}01`, to: today },
        { key: 'last_month', label: 'Last month', from: `${lastMonthEnd.slice(0, 8)}01`, to: lastMonthEnd },
        { key: '90d', label: 'Last 90 days', from: addDays(today, -89), to: today },
        { key: 'year', label: 'This year', from: `${today.slice(0, 4)}-01-01`, to: today },
      ];
    },

    activeRange() {
      const { date_from: from, date_to: to } = this.filters;
      return this.quickRanges.find((r) => r.from === from && r.to === to)?.key || null;
    },

    periodChanged() {
      return this.activeRange !== 'month';
    },

    dayBefore() {
      return this.filters.date_from ? addDays(this.filters.date_from, -1) : '';
    },
  },

  watch: {
    'filters.date_from'() { this.onDateChange(); },
    'filters.date_to'() { this.onDateChange(); },
  },

  methods: {
    onDateChange() {
      const { date_from: from, date_to: to } = this.filters;
      if (from && to && from > to) return;
      this.onFilterChange();
    },

    applyRange(range) {
      this.applyingFilters = true;
      this.filters.date_from = range.from;
      this.filters.date_to = range.to;
      this.$nextTick(() => {
        this.applyingFilters = false;
        this.onFilterChange();
      });
    },

    periodParams() {
      const { date_from: from, date_to: to } = this.filters;
      return { ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) };
    },

    periodText() {
      return `Period: ${this.formatDate(this.filters.date_from)} – ${this.formatDate(this.filters.date_to)}`;
    },

    trendLabel(date) {
      const [year, month, day] = String(date).split('-');
      if (this.summary.trend_unit === 'month') {
        return new Date(Number(year), Number(month) - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
      }
      return `${day}/${month}`;
    },

    drawTrendChart(canvas, inLabel = 'In', outLabel = 'Out') {
      const trend = this.summary.trend;
      if (!trend.length) return;
      const bar = { borderRadius: 4, maxBarThickness: 18 };
      this.drawChart('trend', canvas, {
        type: 'bar',
        data: {
          labels: trend.map((t) => this.trendLabel(t.date)),
          datasets: [
            { label: inLabel, data: trend.map((t) => Number(t.in_qty) || 0), backgroundColor: IN_COLOR, ...bar },
            { label: outLabel, data: trend.map((t) => Number(t.out_qty) || 0), backgroundColor: OUT_COLOR, ...bar },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkip: true } },
            y: { beginAtZero: true, grid: { color: '#F0F2F5' }, ticks: { font: { size: 10 }, precision: 0 } },
          },
        },
      });
    },
  },
};
